// frontend/src/pages/marketplace/MarketplacePage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, 
  ShoppingBag, 
  Heart, 
  Package, 
  TrendingUp,
  ChevronRight,
  Home,
  Briefcase,
  Star,
  AlertCircle,
  Shield,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import MarketplaceTab from '../../components/community/MarketplaceTab';
import BusinessManagementTab from '../../components/community/BusinessManagementTab';
import MyOrdersPage from './MyOrdersPage';
import FavoritesPage from './FavoritesPage';
import useAuthStore from '../../store/authStore';

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('browse');

  const tabs = [
    { id: 'browse', label: 'Browse', icon: Store, color: 'primary' },
    { id: 'mybusinesses', label: 'My Businesses', icon: Briefcase, color: 'blue' },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, color: 'green' },
    { id: 'favorites', label: 'Favorites', icon: Heart, color: 'red' },
  ];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'browse':
        return <MarketplaceTab isFullPage={true} />;
      case 'mybusinesses':
        return <BusinessManagementTab />;
      case 'orders':
        return <MyOrdersPage />;
      case 'favorites':
        return <FavoritesPage />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5]">
      {/* Compact Header */}
      <div className="bg-[#E8ECF5] border-b border-primary-100/50 shadow-neomorph sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-2.5 md:py-3">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="mr-2 p-1.5 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all"
            >
              <ChevronRight className="h-4 w-4 text-gray-600 rotate-180" />
            </button>
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 mr-2">
                <Store className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <div>
                <div className="flex items-center">
                  <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5" />
                  <h1 className="text-sm sm:text-base md:text-lg font-bold text-gray-800">
                    Marketplace
                  </h1>
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 ml-2.5 hidden xs:block">
                  Discover businesses and advertise your services
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== SAFETY NOTICE - Compact ========== */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-2 sm:pt-2.5">
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 sm:py-2 flex items-center gap-2 shadow-sm">
          <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 flex-shrink-0" />
          <p className="text-[10px] sm:text-xs text-amber-800 leading-tight">
            <span className="font-semibold">⚠️ Safety Notice:</span> Fastel is an advertising platform only. 
            We don't facilitate or guarantee transactions between users. 
            <span className="hidden xs:inline"> Always verify sellers before making payments or sharing information.</span>
            <button 
              onClick={() => window.location.href = '/terms-of-service'}
              className="text-amber-700 font-medium hover:text-amber-900 underline ml-0.5"
            >
              Learn more
            </button>
          </p>
        </div>
      </div>

      {/* Compact Tab Navigation - All tabs visible */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-2 sm:pt-2.5">
        <div className="bg-white rounded-xl shadow-neomorph p-0.5 border border-primary-100/30">
          <nav className="flex justify-around gap-0.5">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex-1 px-1.5 py-1.5 sm:py-2 rounded-lg font-medium text-[9px] sm:text-xs flex items-center justify-center transition-all whitespace-nowrap
                    ${isActive
                      ? `bg-${tab.color}-50 text-${tab.color}-600 shadow-neomorph-inset border border-${tab.color}-100`
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1 ${
                    isActive ? `text-${tab.color}-600` : 'text-gray-500'
                  }`} />
                  <span className="hidden xs:inline">{tab.label}</span>
                  <span className="xs:hidden">
                    {tab.id === 'browse' ? 'Browse' : 
                     tab.id === 'mybusinesses' ? 'Biz' :
                     tab.id === 'orders' ? 'Orders' : 'Favs'}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content - Reduced padding */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-3 pb-20 sm:pb-4">
        {renderTabContent()}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default MarketplacePage;